import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "src/components/Pagination/Pagination";
import { DEFAULT_ADMIN_PAGE_SIZE } from "src/constants";
import Page from "src/layouts/Page";
import ContentTable, {
	ContentTableItem,
	ContentTableColumn
} from "src/components/ContentTable/ContentTable";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useAdminGroupsPaged from "../../hooks/useAdminGroupsPaged";
import useDeleteGroup from "../../hooks/useDeleteGroup";
import { mapGroupsToContentTableItems } from "../../utils/mapGroupsToContentTableItems";

const groupColumns: ContentTableColumn[] = [
	{
		key: "memberCount",
		label: "Members",
		render: (item) => String(item.data?.memberCount ?? 0)
	},
	{
		key: "lastUpdated",
		label: "Last Updated",
		render: (item) => {
			if (!item.lastUpdated) return "—";
			return new Date(item.lastUpdated).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "short",
				year: "numeric"
			});
		}
	}
];

const Groups = () => {
	const navigate = useNavigate();
	const [page, setPage] = useState(1);
	const [itemToDelete, setItemToDelete] = useState<ContentTableItem | null>(
		null
	);

	const { data, isLoading, isFetching, isError } = useAdminGroupsPaged(
		page,
		DEFAULT_ADMIN_PAGE_SIZE
	);
	const { mutate: deleteGroup, isLoading: isDeleting } = useDeleteGroup();

	const items = mapGroupsToContentTableItems(data?.results ?? []);
	const totalCount = data?.count ?? 0;

	if (isError) {
		return (
			<Page>
				<p className="text-sm text-red-500">
					Something went wrong loading groups.
				</p>
			</Page>
		);
	}

	return (
		<Page>
			<ContentTable
				items={items}
				columns={groupColumns}
				isLoading={isLoading || isFetching}
				showCreatedBy={false}
				showLastUpdated={false}
				showOrder={false}
				showStatus={false}
				onRowClick={(item) => navigate(`/users/groups/${item.id}`)}
				onDelete={(item) => setItemToDelete(item)}
				createPath="/users/groups/create"
				createLabel="Create Group"
			/>

			<Pagination
				page={page}
				pageSize={DEFAULT_ADMIN_PAGE_SIZE}
				totalCount={totalCount}
				onPageChange={setPage}
				itemLabel="groups"
			/>

			<ConfirmationModal
				isOpen={itemToDelete !== null}
				title="Delete group?"
				description="This action cannot be undone."
				onCancel={() => {
					if (!isDeleting) setItemToDelete(null);
				}}
				cancelBtnProps={{ disabled: isDeleting }}
				onSubmit={() => {
					if (itemToDelete) {
						deleteGroup(Number(itemToDelete.id), {
							onSuccess: () => setItemToDelete(null)
						});
					}
				}}
				submitBtnProps={{ isLoading: isDeleting }}
			/>
		</Page>
	);
};

export default Groups;
